import { z } from 'zod';

/**
 * Web Push (ADR 0073) — geteilte Verträge zwischen Browser und API.
 *
 * Die Feldnamen `endpoint`/`keys.p256dh`/`keys.auth` sind **nicht frei gewählt**:
 * genau so liefert `PushSubscription.toJSON()` im Browser, und genau so erwartet
 * es `web-push` im Backend. Deshalb wird die Struktur unverändert durchgereicht.
 */

/** Ein Push-Abo, wie der Browser es ausgibt. */
export const pushSubscriptionInputSchema = z.object({
  /** Zustell-URL des Push-Dienstes. Reale Endpoints sind < 500 Zeichen. */
  endpoint: z.string().url().max(2000),
  keys: z.object({
    p256dh: z.string().min(1).max(255),
    auth: z.string().min(1).max(255),
  }),
  /** Grobe Gerätekennung für die Geräteliste („Chrome auf Windows"). */
  deviceLabel: z.string().max(120).nullish(),
});
export type PushSubscriptionInput = z.infer<typeof pushSubscriptionInputSchema>;

/** Abmelden: der Browser kennt nur seinen Endpoint, keine Datensatz-Id. */
export const pushUnsubscribeSchema = z.object({
  endpoint: z.string().url().max(2000),
});
export type PushUnsubscribeInput = z.infer<typeof pushUnsubscribeSchema>;

/**
 * Was die Oberfläche wissen muss, bevor sie den Schalter zeigt: ist Push
 * überhaupt eingerichtet (VAPID-Schlüssel hinterlegt), mit welchem öffentlichen
 * Schlüssel abonniert wird, und wie viele Geräte dieser Nutzer bereits hat.
 */
export const pushStatusResponseSchema = z.object({
  isConfigured: z.boolean(),
  /**
   * Warum die Einrichtung unbrauchbar ist (z. B. Schlüsselpaar aus zwei
   * verschiedenen Läufen). `null`, wenn alles passt oder Push bewusst nicht
   * eingerichtet ist — sonst bliebe der Grund nur im Server-Log.
   */
  configError: z.string().nullable(),
  /** `null`, solange keine VAPID-Schlüssel konfiguriert sind. */
  publicKey: z.string().nullable(),
  devices: z.array(
    z.object({
      id: z.string().uuid(),
      deviceLabel: z.string().nullable(),
      lastSuccessAt: z.string().datetime().nullable(),
      createdAt: z.string().datetime(),
    }),
  ),
});
export type PushStatusResponse = z.infer<typeof pushStatusResponseSchema>;

/**
 * Ergebnis des Selbsttests. Anders als der normale Versand (best-effort, still)
 * meldet der Test **jeden** Fehlschlag im Klartext zurück — nur so lässt sich
 * eine frische Einrichtung überhaupt prüfen: „0 Geräte" (nicht abonniert) ist
 * etwas anderes als „403 vom Push-Dienst" (Schlüssel passen nicht zusammen).
 */
export const pushTestResponseSchema = z.object({
  /** Angesprochene Geräte des eigenen Nutzers. */
  deviceCount: z.number().int(),
  sent: z.number().int(),
  /** Klartext je fehlgeschlagenem Gerät (Statuscode + Meldung des Dienstes). */
  errors: z.array(z.string()),
});
export type PushTestResponse = z.infer<typeof pushTestResponseSchema>;
